import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { addDays } from 'date-fns';
import type { CSSProperties } from 'react';
import { useEffect, useEffectEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  BrandL,
  BrandXS,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip/index.tsx';
import { CornerBadge } from '@sorare/core/src/components/CornerBadge/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog';
import { TimeLabel } from '@sorare/core/src/components/labels/TimeLabel/index.tsx';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion.ts';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp.ts';
import {
  LIFECYCLE,
  useLifecycle,
} from '@sorare/core/src/hooks/useLifecycle.ts';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount.ts';
import { ensureHexColor } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';
import { useCommonGameSet } from '@sorare/core/src/set';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { BundledPurchasableItem } from '#src/components/commonCard/BuyPacks/GemsPack/BundledPurchasableItem/index.tsx';
import { VoucherPaymentDialog } from '#src/components/commonCard/BuyPacks/GemsPack/VoucherPaymentDialog/index.tsx';
import { RevealPackCardsDialog } from '#src/components/commonCard/RevealPackCardsDialog/index.tsx';

import type {
  WelcomePackDialogQuery,
  WelcomePackDialogQueryVariables,
  WelcomePackPurchasedOfferQuery,
  WelcomePackPurchasedOfferQueryVariables,
} from './__generated__/index.graphql.ts';

const StyledDialod = styled(Dialog)`
  border: none;
  &::after {
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 2vh;
    background: radial-gradient(
      ellipse 50% 2vh at bottom,
      var(--c-set-surface-primary) 0%,
      var(--c-set-surface-primary) 20%,
      transparent 100%
    );
    filter: blur(8px);
    content: '';
  }
  @media ${laptopAndAbove} {
    box-shadow: 0 0 30px var(--c-set-surface-primary);
    &:after {
      display: none;
    }
  }
`;
const DialogBody = styled(Vertical)`
  display: flex;
  flex-direction: column;
  text-align: center;
  position: relative;
  padding: var(--double-unit);
  min-height: 100%;
`;
const CloseButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  position: relative;
  z-index: 1;
`;
const Content = styled(Vertical)`
  position: relative;
  z-index: 1;
  align-items: center;
  flex: 1;
  justify-content: space-between;
`;

const Header = styled(Vertical)`
  justify-content: flex-start;
`;

const BundleSection = styled.div`
  display: inline-flex;
  align-items: flex-end;
  gap: var(--double-unit);
  margin: auto;
  max-width: 100%;
  overflow: visible;
`;

const BundleCardWrapper = styled.div`
  position: relative;
  overflow: visible;
  width: calc(100% / 3);
`;

/* Separate from CardRoot so filter: drop-shadow doesn't conflict with backdrop-filter */
const CardShadowWrapper = styled.div`
  filter: drop-shadow(var(--bundle-shadow, 0 0 0 transparent));
`;

/* Positioned outside CardRoot to avoid clip-path clipping */
const Label = styled.div`
  position: absolute;
  z-index: 1;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  text-transform: uppercase;
`;

const Footer = styled(Vertical).attrs({ gap: 0.5, center: true })`
  padding: 0 var(--double-unit) var(--quadruple-unit);
`;

const ButtonWrapper = styled(ButtonBase)`
  position: relative;
  display: inline-block;
`;

const DiscountBadgeWrapper = styled.div`
  position: absolute;
  bottom: 100%;
  transform: translateY(40%);
  right: 0;
  z-index: 1;
`;

const discountedOfferTextGradient =
  'linear-gradient(218deg, #FFF 1.37%, #FCFF63 36.66%, #C26900 101.57%)';

const labels = [
  {
    label: <FormattedMessage defaultMessage="Advanced" />,
    color: 'common' as const,
    borderGradient: 'linear-gradient(-45deg, #868A8A 0%, #E8ECEE 100%)',
  },
  {
    label: <FormattedMessage defaultMessage="Elite" />,
    color: 'glow' as const,
    borderGradient:
      'linear-gradient(-5deg, #EAE3DA 5%, #BFE6EF 34%, #E1E3F5 69%, #E6BADE 88%, #E8F6EF 100%)',
  },
  {
    label: <FormattedMessage defaultMessage="Legendary" />,
    color: 'fire' as const,
    borderGradient: 'linear-gradient(180deg, #FF4E22 17%, #FCFF63 85%)',
    glow: '0 0 10px rgba(255, 78, 34, 1)',
  },
];

const WELCOME_PACK_DIALOG_QUERY = gql`
  query WelcomePackDialogQuery($sport: Sport!) {
    currentUser {
      slug
      availableVoucher(sport: $sport) {
        id
        displayName
        endDate
        discountedOffer
        purchasableItems {
          slug
          ... on BundledPurchasableItem {
            slug
            ...BundledPurchasableItem_bundledPurchasableItem
          }
        }
        buyAllPurchasableItem {
          slug
          id
          cardPaymentProvider
          price {
            ...useMonetaryAmount_monetaryAmount
          }
          ... on BundledPurchasableItem {
            slug
            discountBasisPoints
            oldPrice {
              ...useMonetaryAmount_monetaryAmount
            }
          }
        }
      }
    }
  }
  ${BundledPurchasableItem.fragments.bundledPurchasableItem}
  ${useMonetaryAmount.fragments.monetaryAmount}
` as TypedDocumentNode<WelcomePackDialogQuery, WelcomePackDialogQueryVariables>;

const WELCOME_PACK_PURCHASED_OFFER_QUERY = gql`
  query WelcomePackPurchasedOfferQuery($offerId: String!) {
    tokens {
      primaryOffer(id: $offerId) {
        id
        purchasedItems {
          slug
          ... on PackConfigPurchasableItem {
            slug
            pack {
              id
              cards {
                slug
                ...RevealPackCardsDialog_anyCardInterface
              }
            }
            cardPack {
              slug
              pictureUrl
              primaryColor: color(variant: PRIMARY)
              secondaryColor: color(variant: SECONDARY)
              cardSet {
                slug
              }
            }
          }
        }
      }
    }
  }
  ${RevealPackCardsDialog.fragments.anyCardInterface}
` as TypedDocumentNode<
  WelcomePackPurchasedOfferQuery,
  WelcomePackPurchasedOfferQueryVariables
>;

type Props = {
  onClose?: () => void;
};

export const WelcomePackDialog = ({ onClose: onCloseProps }: Props) => {
  const {
    flags: { useWelcomePromoPack = false },
  } = useFeatureFlags();
  const closeDialog = useCloseDialog();
  const { postMessage, isIosApp } = useIsMobileApp();

  const onClose = () => {
    if (isIosApp) {
      postMessage('closeCurrentWebview');
    } else {
      closeDialog(DialogKey.welcomePack);
      onCloseProps?.();
    }
  };
  const { visual } = useCommonGameSet();
  const { welcomePackDialog } = visual;
  const { toMonetaryAmount } = useMonetaryAmount();
  const { sport } = useSportContext();
  const { data, loading } = useQuery(WELCOME_PACK_DIALOG_QUERY, {
    variables: { sport },
    skip: !useWelcomePromoPack,
  });

  const [currentPackIndex, setCurrentPackIndex] = useState(0);
  const [revealOpen, setRevealOpen] = useState(true);
  const [buyAllDialogOpened, setBuyAllDialogOpened] = useState(false);

  const [fetch, { data: purchasedOfferData }] = useLazyQuery(
    WELCOME_PACK_PURCHASED_OFFER_QUERY
  );

  const purchasedPacks =
    purchasedOfferData?.tokens?.primaryOffer?.purchasedItems?.filter(item =>
      isType(item, 'PackConfigPurchasableItem')
    ) ?? [];

  const currentPack =
    purchasedPacks[currentPackIndex] &&
    isType(purchasedPacks[currentPackIndex], 'PackConfigPurchasableItem')
      ? purchasedPacks[currentPackIndex]
      : null;

  const revealDialogOpen = !!currentPack;

  const handleCloseRevealDialog = () => setRevealOpen(false);

  const handleRevealDialogClosed = () => {
    if (currentPackIndex < purchasedPacks.length - 1) {
      setCurrentPackIndex(prev => prev + 1);
      setRevealOpen(true);
    } else {
      setCurrentPackIndex(0);
      onClose();
    }
  };

  const handlePackPurchased = (offerId: string) => {
    fetch({
      variables: { offerId: idFromObject(offerId!) },
    });
  };

  const { currentUser } = data || {};
  const bundledItems =
    currentUser?.availableVoucher?.purchasableItems.filter(item =>
      isType(item, 'BundledPurchasableItem')
    ) || [];
  const buyAllPurchasableItem =
    currentUser?.availableVoucher?.buyAllPurchasableItem;
  const buyAllPrice = toMonetaryAmount(buyAllPurchasableItem?.price);
  const bundledBuyAll = isType(buyAllPurchasableItem, 'BundledPurchasableItem')
    ? buyAllPurchasableItem
    : null;
  const buyAllOldPrice = bundledBuyAll?.oldPrice
    ? toMonetaryAmount(bundledBuyAll.oldPrice)
    : null;
  let discountPercent: number | null = null;
  let discountFromBasisPoints = false;
  if (bundledBuyAll?.discountBasisPoints) {
    discountPercent = Math.round(bundledBuyAll.discountBasisPoints / 100);
    discountFromBasisPoints = true;
  } else if (buyAllOldPrice && buyAllOldPrice.eurCents > 0) {
    const computed = Math.round(
      (1 - buyAllPrice.eurCents / buyAllOldPrice.eurCents) * 100
    );
    if (computed > 0) discountPercent = computed;
  }
  const { main: buyAllPriceLabel } = useAmountWithConversion({
    monetaryAmount: buyAllPrice,
  });
  const { main: buyAllOldPriceLabel } = useAmountWithConversion({
    monetaryAmount: buyAllOldPrice,
  });
  const voucherId = currentUser?.availableVoucher?.id;
  const endDate = currentUser?.availableVoucher?.endDate;
  const discountedOffer = !!currentUser?.availableVoucher?.discountedOffer;

  const { lifecycle, update } = useLifecycle();

  const recordAutoOpenShown = useEffectEvent(() => {
    if (!voucherId) return;
    update(LIFECYCLE.welcomePackAutoOpen, {
      ...lifecycle?.[LIFECYCLE.welcomePackAutoOpen],
      [voucherId]: addDays(Date.now(), 2),
    });
  });

  useEffect(() => {
    recordAutoOpenShown();
  }, [voucherId]);

  if ((!useWelcomePromoPack || !voucherId) && !loading) return null;

  const dialogBackground =
    welcomePackDialog?.[discountedOffer ? 'discounted' : 'regular'];

  return (
    <>
      <StyledDialod
        maxWidth="xs"
        open
        onClose={onClose}
        hideHeader
        style={dialogBackground}
        skipTransition
      >
        {({ CloseButton }) => {
          return (
            <DialogBody>
              <CloseButtonWrapper>
                <CloseButton onClose={onClose} transparent />
              </CloseButtonWrapper>
              <Content gap={4}>
                <Header gap={2}>
                  <Vertical center>
                    <TimeLabel
                      state="countdown"
                      color={discountedOffer ? 'purple' : undefined}
                    >
                      <TimeLeft time={endDate || new Date()} />
                    </TimeLabel>
                    <BrandL className="text-center text-pretty">
                      {discountedOffer && discountPercent ? (
                        <FormattedMessage
                          defaultMessage="Same offer, {br}<gradient>{percent, number, ::percent} discount!</gradient>"
                          values={{
                            percent: discountPercent / 100,
                            br: <br />,
                            gradient: chunks => (
                              <span
                                className="text-gradient-color"
                                style={
                                  {
                                    '--text-gradient-color':
                                      discountedOfferTextGradient,
                                  } as CSSProperties
                                }
                              >
                                {chunks}
                              </span>
                            ),
                          }}
                        />
                      ) : (
                        <FormattedMessage defaultMessage="Claim your exclusive offer!" />
                      )}
                    </BrandL>
                  </Vertical>
                  <BodyS className="text-center">
                    {discountedOffer ? (
                      <>
                        <FormattedMessage
                          defaultMessage="Get your original exclusive offer at a <gradient>reduced price!</gradient>"
                          values={{
                            gradient: chunks => (
                              <BodyS
                                as="span"
                                bold
                                className="text-gradient-color"
                                style={
                                  {
                                    '--text-gradient-color':
                                      discountedOfferTextGradient,
                                  } as CSSProperties
                                }
                              >
                                {chunks}
                              </BodyS>
                            ),
                          }}
                        />
                        <br />
                      </>
                    ) : null}
                    <FormattedMessage
                      defaultMessage="Select an offer or unlock them all.{br}Remaining offers expire upon purchase*"
                      values={{ br: <br /> }}
                    />
                  </BodyS>
                </Header>

                <BundleSection>
                  {!voucherId || !!loading ? (
                    <div style={{ height: 296 }} />
                  ) : (
                    bundledItems.map((bundledItem, index) => (
                      <BundleCardWrapper key={bundledItem.slug}>
                        <Label>
                          <Chip size="small" brand color={labels[index].color}>
                            {labels[index].label}
                          </Chip>
                        </Label>
                        <CardShadowWrapper
                          style={
                            labels[index]?.glow
                              ? ({
                                  '--bundle-shadow': labels[index].glow,
                                } as CSSProperties)
                              : undefined
                          }
                        >
                          <BundledPurchasableItem
                            bundledPurchasableItem={bundledItem}
                            onPackPurchased={handlePackPurchased}
                            voucherId={voucherId}
                            borderGradient={labels[index]?.borderGradient}
                          />
                        </CardShadowWrapper>
                      </BundleCardWrapper>
                    ))
                  )}
                </BundleSection>
                <ButtonWrapper onClick={() => setBuyAllDialogOpened(true)}>
                  {!!discountPercent && (
                    <DiscountBadgeWrapper>
                      <CornerBadge color="linear-gradient(188deg, rgb(255, 255, 255) 1.37%, rgb(252, 255, 99) 36.66%, rgb(194, 105, 0) 101.57%)">
                        <BrandXS className="text-center">
                          {discountFromBasisPoints ? (
                            <FormattedMessage
                              defaultMessage="-{percent, number, ::percent} discount**"
                              values={{ percent: discountPercent / 100 }}
                            />
                          ) : (
                            <FormattedMessage
                              defaultMessage="-{percent, number, ::percent} discount{br}of original price"
                              values={{
                                percent: discountPercent / 100,
                                br: <br />,
                              }}
                            />
                          )}
                        </BrandXS>
                      </CornerBadge>
                    </DiscountBadgeWrapper>
                  )}
                  <Button
                    size="medium"
                    asSpan
                    style={{
                      padding:
                        discountPercent && !discountFromBasisPoints
                          ? 'var(--quadruple-unit) var(--double-unit)'
                          : undefined,
                    }}
                  >
                    <Vertical gap={0} center>
                      <FormattedMessage
                        defaultMessage="Get all {count} for {price}"
                        values={{
                          count: bundledItems.length,
                          price: buyAllPriceLabel,
                        }}
                      />
                      {!!buyAllOldPrice && !buyAllOldPrice.isZero && (
                        <LabelS color="var(--c-nd-600)">
                          <FormattedMessage
                            defaultMessage="instead of {oldPrice}"
                            values={{ oldPrice: buyAllOldPriceLabel }}
                          />
                        </LabelS>
                      )}
                    </Vertical>
                  </Button>
                </ButtonWrapper>

                <Footer>
                  <LabelS color="var(--c-nd-600)">
                    <FormattedMessage defaultMessage="*Each exclusive deal can only be claimed once. After your purchase, all remaining offers will expire." />
                  </LabelS>
                  <LabelS
                    color={
                      !!discountPercent && !!discountFromBasisPoints
                        ? 'transparent'
                        : 'var(--c-nd-600)'
                    }
                  >
                    <FormattedMessage defaultMessage="**Global discount applied to the total combined reference prices of the three offers." />
                  </LabelS>
                </Footer>
              </Content>
            </DialogBody>
          );
        }}
      </StyledDialod>
      {buyAllPurchasableItem ? (
        <VoucherPaymentDialog
          open={buyAllDialogOpened}
          onClose={() => setBuyAllDialogOpened(false)}
          purchasableItemId={buyAllPurchasableItem.id}
          cardPaymentProvider={buyAllPurchasableItem.cardPaymentProvider}
          voucherId={voucherId}
          price={buyAllPrice}
          onPackPurchased={handlePackPurchased}
        />
      ) : null}
      {revealDialogOpen ? (
        <RevealPackCardsDialog
          key={currentPack.pack?.id}
          open={revealOpen}
          onClosed={handleRevealDialogClosed}
          cards={currentPack.pack?.cards ?? []}
          onContinue={handleCloseRevealDialog}
          packId={currentPack.pack?.id ?? ''}
          packPictureUrl={currentPack.cardPack?.pictureUrl || ''}
          primaryColor={ensureHexColor(
            currentPack.cardPack?.primaryColor || ''
          )}
          secondaryColor={ensureHexColor(
            currentPack.cardPack?.secondaryColor || ''
          )}
          cardSetSlug={currentPack.cardPack?.cardSet?.slug || ''}
          showPlayerDetails
        />
      ) : null}
    </>
  );
};
